import apiBaseUrl from '../../utils/config.js';

class patientInfoService {
    constructor() {
        this.baseUrl = `${apiBaseUrl}/patientinfo`;
    }

    // ✅ Utility method to get JWT from localStorage
    getToken() {
        return localStorage.getItem("jwt");
    }

    // ✅ Register user credentials
    async registerCredentials(registerRequest) {
        try {
            const response = await fetch(`${this.baseUrl}/register/credentials`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(registerRequest),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Unknown error occurred');
            }

            return data;
        } catch (error) {
            console.error('Registration error:', error.message);
            throw error;
        }
    }

    // ✅ Login function
    async login(loginRequest) {
        try {
            const response = await fetch(`${this.baseUrl}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(loginRequest),
            });

            if (!response.ok) {
                throw new Error(await response.text());
            }

            const result = await response.json();
            console.log("Login successful. Token:", result.token);

            // ✅ Save JWT to localStorage
            localStorage.setItem("jwt", result.token);
            localStorage.setItem("username", result.username);

            return result;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }

async savePatientDetails(patientId, username, detailsRequest) {
    try {
        const token = this.getToken();

        const queryParams = new URLSearchParams();
        if (patientId) queryParams.append("patientId", patientId);
        if (username) queryParams.append("username", username);

        const response = await fetch(`${this.baseUrl}/update-details?${queryParams.toString()}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`  // Attach JWT token
            },
            credentials: 'include',  // Ensure cookies are sent
            body: JSON.stringify(detailsRequest),
        });

        // Check if the response is not okay
        if (!response.ok) {
            // Attempt to parse the error response as JSON
            let errorText;
            try {
                const errorResponse = await response.json();
                errorText = errorResponse.error || 'Failed to update patient details';
            } catch (parseError) {
                // Fallback to plain text if parsing fails
                errorText = await response.text();
            }
            throw new Error(errorText);
        }

        // If the response is okay, return the parsed JSON
        const responseData = await response.json();
        console.log('Update successful:', responseData.message);
        return responseData;
    } catch (error) {
        console.error('Update error:', error.message);
        throw error;
    }
}


// ✅ Update patient username, password, and email
async updatePatientAccount(patientId, newUsername, newPassword, newEmail) {
    try {
        const token = this.getToken();

        const response = await fetch(`${this.baseUrl}/update-account?patientId=${patientId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                username: newUsername,
                password: newPassword,
                email: newEmail, // ✅ include email
            }),
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorResponse = await response.json();
                errorMessage = errorResponse.error || 'Update failed';
            } catch {
                errorMessage = await response.text();
            }
            throw new Error(errorMessage);
        }
    } catch (error) {
        console.error('Error updating account:', error.message);
        throw error;
    }
}



    // ✅ Fetch patient by ID
    async getPatientById(patientId) {
        try {
            const token = this.getToken();
            const response = await fetch(`${this.baseUrl}/${patientId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`  // ✅ Attach JWT token
                }
            });

            if (!response.ok) {
                throw new Error(await response.text());
            }

            return await response.json();
        } catch (error) {
            console.error('Fetch patient error:', error);
            throw error;
        }
    }

    // ✅ Search patients by name
    async searchPatients(firstName, lastName) {
        try {
            const token = this.getToken();
            const queryParams = new URLSearchParams();
            if (firstName) queryParams.append('firstName', firstName);
            if (lastName) queryParams.append('lastName', lastName);

            const response = await fetch(`${this.baseUrl}/search?${queryParams.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`  // ✅ Attach JWT token
                }
            });

            if (!response.ok) {
                throw new Error(await response.text());
            }

            return await response.json();
        } catch (error) {
            console.error('Search error:', error);
            throw error;
        }
    }

    // ✅ Fetch all patients
    async getAllPatients() {
        try {
            const token = this.getToken();
            const response = await fetch(`${this.baseUrl}/all`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`  // ✅ Attach JWT token
                }
            });

            if (!response.ok) {
                throw new Error(await response.text());
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching all patients:', error);
            throw error;
        }
    }

    // ✅ Logout user
    async logout() {
        localStorage.removeItem("jwt");
        localStorage.removeItem("username");
        console.log("User logged out");
    }

    // ✅ Fetch patient by username
async getPatientByUsername(username) {
    try {
        const token = this.getToken();
        const response = await fetch(`${this.baseUrl}/by-username/${username}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error(await response.text());
        }

        return await response.json(); // returns full patient object
    } catch (error) {
        console.error('Error fetching patient by username:', error);
        throw error;
    }
}

 // ✅ Request password reset code (send email)
  async requestResetCode(username) {
    const response = await fetch(`${this.baseUrl}/request-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to send reset code");
    }
    return data;
  }

  // ✅ Verify reset code and update password
  async verifyResetCodeAndChangePassword({ username, code, newPassword }) {
    const response = await fetch(`${this.baseUrl}/verify-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, code, newPassword })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to reset password");
    }
    return data;
  }

}

export default patientInfoService;
